import { UserStorage } from "shared/tools/UserStorage";
import styles from "../../styles/menu-desktop.module.scss";

import { memo, useEffect, useRef, useState } from "react";
import ROLES from "data/Roles";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { Environment } from "shared/core/Environment";
import { AccessManager } from "shared/tools/AccessManager";
import { useLocation, useNavigate } from "react-router";
import GestorLogo from "./GestorLogo";
import { SelectActiveUnit } from "./SelectActiveUnit";

import classNames from "classnames/bind";
import { InputSearch } from "shared/components/InputSearch";
import { If } from "shared/components/control-statements";
import { WorkingTime } from "shared/utils/WorkingTime";
import { useOnClickOutside } from "shared/hooks/useOnClickOutside";
import { isMobile } from "react-device-detect";
const css = classNames.bind(styles);

const Chip = ({ value }) => {
  if (!value) return null;

  return <div className={styles.count}>{value > 10 ? "+10" : value}</div>;
};

export default function MenuDesktop({ ordersCount }) {
  const configurations = EnvironmentStorage.getConfigurations();

  const userRole = UserStorage.getLoggedUser()?.role;

  const [visible, setVisible] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMenus, setFilteredMenus] = useState([]);

  const environment = EnvironmentStorage.getEnvironment();
  const isBrandSelected = environment?.type === "BRAND";
  const isUnitSelected =
    environment?.type !== "BRAND" && environment?.type !== "HOLDING";

  const { pathname } = useLocation();

  const enabledChangeBrand =
    pathname.startsWith("/pedidos") ||
    (pathname.startsWith("/configuracoes") &&
      !pathname.startsWith("/configuracoes/personalizacao-do-inicio")) ||
    pathname.startsWith("/integracoes") ||
    pathname.startsWith("/usuarios");

  const ref = useRef(null);

  const checkRoles = (roles) => {
    let userRole = UserStorage.getLoggedUser()?.role;
    if (!userRole) userRole = ROLES.SHOPKEEPER;

    const allowRoute = roles.some((r) => r === userRole);

    return allowRoute;
  };

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const p = location.pathname;

    if (p === (userRole === ROLES.SAC ? "/base-de-dados" : "/"))
      setCurrentMenu("Base de dados");

    if (p.startsWith("/relatorios")) setCurrentMenu("Relatórios");

    if (
      p.startsWith("/fluxos") ||
      p.startsWith("/beneficios") ||
      p.startsWith("/fidelidade") ||
      p.startsWith("/avaliacoes")
    )
      setCurrentMenu("Fidelização e Marketing");

    if (
      p.startsWith("/pedidos") ||
      p.startsWith("/configuracoes") ||
      p.startsWith("/atendimento")
    )
      setCurrentMenu("Frentes de venda");

    if (p.startsWith("/integracoes") || p.startsWith("/usuarios"))
      setCurrentMenu("Sistema");
  }, [location, userRole]);

  const menus = [
    {
      name: "Base de dados",
      icon: <i className="fal fa-database" />,
      path: userRole === ROLES.SAC ? "/base-de-dados" : "/",
      visible: checkRoles([
        ROLES.SUPPORT,
        ROLES.ADMIN,
        ROLES.MARKETING,
        ROLES.SAC,
      ]),
      submenu: [],
    },
    {
      name: "Relatórios",
      icon: <i className="fal fa-chart-waterfall" />,
      visible: checkRoles([ROLES.SUPPORT, ROLES.ADMIN, ROLES.MARKETING]),
      submenu: [
        {
          name: "Clientes",
          path: "/relatorios/clientes",
          visible: checkRoles([ROLES.SUPPORT, ROLES.ADMIN, ROLES.MARKETING]),
        },
        {
          name: "Pedidos",
          path: "/relatorios/pedidos",
          visible: checkRoles([ROLES.SUPPORT, ROLES.ADMIN, ROLES.MARKETING]),
        },
        {
          name: "Marketing",
          path: "/relatorios/marketing",
          visible: checkRoles([ROLES.SUPPORT, ROLES.ADMIN, ROLES.MARKETING]),
        },
        {
          name: "Fidelização",
          path: "/relatorios/fidelizacao",
          visible: checkRoles([ROLES.SUPPORT, ROLES.ADMIN, ROLES.MARKETING]),
        },
      ],
    },
    {
      name: "Fidelização e Marketing",
      icon: <i className="fal fa-rocket" />,
      visible: checkRoles([
        ROLES.SUPPORT,
        ROLES.ADMIN,
        ROLES.MARKETING,
        ROLES.SAC,
      ]),
      submenu: [
        {
          name: "Fluxos",
          path: "/fluxos",
          visible: checkRoles([
            ROLES.SUPPORT,
            ROLES.ADMIN,
            ROLES.MARKETING,
            ROLES.SAC,
          ]),
        },
        {
          name: "Benefícios avulsos",
          path: "/beneficios",
          visible: true,
        },
        {
          name: "Programa de fidelidade",
          path: "/fidelidade",
          visible: checkRoles([ROLES.SUPPORT, ROLES.MARKETING, ROLES.ADMIN]),
        },
        {
          name: "Avaliações",
          path: "/avaliacoes",
          visible: checkRoles([
            ROLES.SUPPORT,
            ROLES.MARKETING,
            ROLES.ADMIN,
            ROLES.SAC,
          ]),
        },
        {
          name: "Mensageria",
          path: "/mensageria",
          visible: checkRoles([ROLES.SUPPORT]),
        },
      ],
    },
    {
      name: "Frentes de venda",
      icon: <i className="fal fa-store" />,
      visible: checkRoles([
        ROLES.SUPPORT,
        ROLES.SAC,
        ROLES.ADMIN,
        ROLES.MANAGER,
        ROLES.SHOPKEEPER,
        ROLES.MARKETING,
      ]),
      submenu: [
        {
          name: "Atendimento",
          path: "/atendimento",
          visible: checkRoles([ROLES.SUPPORT]),
        },
        {
          name: "Pedidos",
          path: "/pedidos",
          visible: checkRoles([
            ROLES.SUPPORT,
            ROLES.SAC,
            ROLES.ADMIN,
            ROLES.MANAGER,
            ROLES.SHOPKEEPER,
          ]),
        },
        {
          name: "Configurações",
          path: "/configuracoes",
          visible: true,
        },
      ],
    },
    {
      name: "Sistema",
      icon: <i className="fal fa-sliders" />,
      visible: true,
      submenu: [
        {
          name: "Integrações",
          path: "/integracoes",
          visible: checkRoles([ROLES.ADMIN, ROLES.SUPPORT, ROLES.MANAGER]),
        },
        {
          name: "Usuários",
          path: "/usuarios",
          visible: checkRoles([ROLES.ADMIN, ROLES.SUPPORT]),
        },
        {
          name: "Sair",
          action: () => {
            UserStorage.setAccessToken(null);
            EnvironmentStorage.setEnvironment(null);
            EnvironmentStorage.setConfigurations(null);
            Environment.uninstall();
            localStorage.removeItem("refreshOrdersAuto");
            localStorage.removeItem(
              `${configurations?.parent_alias}-refreshOrdersAuto`,
            );
            AccessManager.setPermissions([]);
            navigate("/entrar");
          },
          visible: true,
        },
      ],
    },
  ];

  useEffect(() => {
    const checkIfIsOpen = () => {
      const environmentConfigurations = EnvironmentStorage.getConfigurations();
      const { working_time, active } = environmentConfigurations || {};

      if (!active || !working_time) {
        setIsOpen(false);
      } else {
        const workingTimeObj = WorkingTime.fromObject(working_time);
        const open = workingTimeObj.isOpen();
        setIsOpen(open);
      }
    };

    checkIfIsOpen();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const searchAllMenus = () => {
        const lowerQuery = searchTerm.toLowerCase();

        return menus.filter(
          (menu) =>
            menu.name.toLowerCase().includes(lowerQuery) ||
            (menu.submenu || []).some((sub) =>
              sub.name.toLowerCase().includes(lowerQuery),
            ),
        );
      };

      const filteredMenus = searchAllMenus();

      setFilteredMenus(filteredMenus);

      setActiveMenu(filteredMenus.map((menu) => menu.name));
    } else {
      setFilteredMenus(menus);
      setActiveMenu(null);
    }
  }, [searchTerm]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu.name && collapsed ? null : menu.name);
    setCollapsed(true);
  };

  const menuRef = useRef(null);

  useOnClickOutside(menuRef, () => setCollapsed(false));

  useEffect(() => {
    if (!collapsed) setActiveMenu(null);
  }, [collapsed]);

  return (
    <div className={css({ menuDesktop: true, collapsed })} ref={menuRef}>
      <div className={styles.menuHeader}>
        <div className={styles.logo} onClick={() => setCollapsed(true)}>
          <GestorLogo />
        </div>
        <div className={styles.bars} onClick={() => setCollapsed(!collapsed)}>
          <i className="fal fa-bars" />
        </div>
      </div>

      <If cond={collapsed}>
        <InputSearch
          design="three"
          placeholder="Buscar por..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          ref={ref}
        />
      </If>
      <If cond={!collapsed}>
        <div
          className={styles.search}
          onClick={() => {
            setCollapsed(true);

            setTimeout(() => {
              if (ref?.current) ref?.current?.focus();
            }, 0);
          }}
        >
          <i className="fal fa-search" />
        </div>
      </If>
      <nav>
        <ul>
          {filteredMenus.map((menu, index) => {
            if (!menu.visible) return null;

            return (
              <li key={menu.name}>
                <div
                  className={css({
                    menuItem: true,
                    active: activeMenu === menu.name,
                    current: currentMenu === menu.name,
                  })}
                  onClick={() => {
                    if (menu.path) {
                      setCollapsed(false);
                      setActiveSubMenu(null);
                      setActiveMenu(menu.name);
                      setCurrentMenu(menu.name);
                      navigate(menu.path);
                    } else toggleMenu(menu);
                  }}
                >
                  <div className="d-flex align-center">
                    <div className={styles.icon}>{menu.icon}</div>
                    <span>{menu.name}</span>
                  </div>
                  <div className="d-flex align-center">
                    <If
                      cond={
                        menu.name === "Frentes de venda" &&
                        ordersCount > 0 &&
                        collapsed
                      }
                    >
                      <Chip value={ordersCount} />
                    </If>
                    <If cond={menu.submenu.length > 0}>
                      <i className={`fas fa-chevron-down ${styles.arrow}`} />
                    </If>
                  </div>
                </div>
                {menu.submenu && (
                  <div
                    className={css({
                      submenu: true,
                      submenuOpen:
                        activeMenu === menu.name ||
                        (activeMenu || []).includes(menu.name),
                      submenuClosed:
                        activeMenu !== menu.name &&
                        !(activeMenu || []).includes(menu.name),
                    })}
                  >
                    {menu.submenu.map((sub) => {
                      const lowerQuery = searchTerm.toLowerCase();

                      if (!sub.visible) return null;
                      if (
                        lowerQuery &&
                        !sub.name.toLowerCase().includes(lowerQuery)
                      )
                        return null;
                      return (
                        <div
                          key={sub.name}
                          className={css({
                            submenuItem: true,
                            active:
                              activeSubMenu === sub.path ||
                              pathname === sub.path,
                          })}
                          onClick={() => {
                            setActiveSubMenu(sub.path);
                            sub.path && navigate(sub.path);
                            setCollapsed(false);
                            sub.action && sub.action();
                          }}
                        >
                          <span>{sub.name}</span>
                          <If
                            cond={
                              sub.name === "Pedidos" &&
                              menu.name === "Frentes de venda" &&
                              ordersCount > 0 &&
                              collapsed
                            }
                          >
                            <Chip value={ordersCount} />
                          </If>
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}

          <If cond={searchTerm && filteredMenus.length === 0}>
            <li>
              <div className={styles.notFound}>
                <span>Nenhum resultado encontrado</span>
              </div>
            </li>
          </If>
        </ul>
      </nav>

      <If cond={enabledChangeBrand}>
        <If cond={collapsed}>
          <div
            className={css({
              selectUnit: true,
              showEnv: UserStorage.isGodMode,
            })}
            onClick={() => setVisible(true)}
          >
            <div className="d-flex align-center">
              <div className={styles.logo}>
                <GestorLogo />
                <If cond={isUnitSelected}>
                  <span className={styles.status}>
                    <div className={css({ dot: true, open: isOpen })} />
                  </span>
                </If>
              </div>
              <div className="d-flex flex-column">
                <p className={styles.brand}>
                  {isUnitSelected
                    ? environment?.brand?.name
                    : environment?.name}
                </p>
                <If cond={isUnitSelected}>
                  <p className={styles.unit}>{environment?.name}</p>
                </If>
              </div>
            </div>

            <div className={styles.vision}>
              <span className={styles.tag}>
                <If cond={isBrandSelected}>VISÃO DE MARCA</If>
                <If cond={isUnitSelected}>VISÃO DE UNIDADE</If>
              </span>
              <span className={styles.arrow}>
                <i className="fas fa-chevron-right" />
              </span>
            </div>
          </div>
        </If>

        <If cond={!collapsed}>
          <div
            className={styles.selectUnitMinified}
            onClick={() => setVisible(true)}
          >
            <div className={styles.logo}>
              <GestorLogo />
              <If cond={isUnitSelected}>
                <span className={styles.status}>
                  <div className={css({ dot: true, open: isOpen })} />
                </span>
              </If>
            </div>
          </div>
        </If>
      </If>

      <If cond={UserStorage.isGodMode && collapsed}>
        <p className={styles.environment}>
          {isUnitSelected ? environment?.brand?.name : environment?.name}
        </p>
      </If>

      <SelectActiveUnit
        visible={visible}
        onClose={() => setVisible(false)}
        direction={isMobile ? "right" : "left"}
      />
    </div>
  );
}
